import { useState } from "react";
import { companyInfo } from "../data/mock";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Mock submission
    setTimeout(() => {
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", company: "", message: "" });
      setIsSubmitting(false);
    }, 1200);
  };

  const inputStyle = {
    width: "100%",
    background: "rgba(123,104,238,0.05)",
    border: "1px solid rgba(123,104,238,0.15)",
    color: "#F0F0FF",
    padding: "16px 20px",
    fontSize: "15px",
    fontFamily: "'Kode Mono', monospace",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.3s ease, background 0.3s ease",
    boxSizing: "border-box",
  };

  return (
    <section
      id="contact"
      className="relative w-full"
      style={{
        background: "#0F1225",
        padding: "120px 7.6923%",
        borderTop: "1px solid rgba(123,104,238,0.08)",
      }}
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-[2px] rounded-full" style={{ background: "#7B68EE" }} />
              <span
                className="text-sm tracking-widest uppercase"
                style={{ color: "#7B68EE", fontFamily: "'Kode Mono', monospace" }}
              >
                Get In Touch
              </span>
            </div>

            <h2
              style={{
                fontFamily: "'Kode Mono', monospace",
                fontWeight: 600,
                fontSize: "clamp(32px, 4vw, 48px)",
                color: "#F0F0FF",
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
                marginBottom: "20px",
              }}
            >
              Let's Build Something
              <br />
              <span style={{ color: "#8585A8" }}>Extraordinary</span>
            </h2>

            <p
              style={{
                fontFamily: "'Kode Mono', monospace",
                fontSize: "16px",
                color: "#8585A8",
                lineHeight: 1.7,
                marginBottom: "48px",
                maxWidth: "480px",
              }}
            >
              Ready to transform your business with cutting-edge technology?
              Reach out and let's discuss your next big project.
            </p>

            {/* Contact Details */}
            <div className="flex flex-col gap-8">
              <div className="flex items-start gap-5">
                <div
                  className="w-12 h-12 flex items-center justify-center flex-shrink-0 rounded-lg"
                  style={{ background: "rgba(123,104,238,0.1)", border: "1px solid rgba(123,104,238,0.2)" }}
                >
                  <Mail size={18} style={{ color: "#A78BFA" }} />
                </div>
                <div>
                  <div
                    className="text-xs uppercase tracking-wider mb-2"
                    style={{
                      fontFamily: "'Kode Mono', monospace",
                      color: "#6B6B8D",
                    }}
                  >
                    Email
                  </div>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="no-underline transition-colors duration-300"
                    style={{
                      fontFamily: "'Kode Mono', monospace",
                      fontSize: "16px",
                      color: "#C4C4E0",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = "#A78BFA"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = "#C4C4E0"; }}
                  >
                    {companyInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div
                  className="w-12 h-12 flex items-center justify-center flex-shrink-0 rounded-lg"
                  style={{ background: "rgba(123,104,238,0.1)", border: "1px solid rgba(123,104,238,0.2)" }}
                >
                  <Phone size={18} style={{ color: "#A78BFA" }} />
                </div>
                <div>
                  <div
                    className="text-xs uppercase tracking-wider mb-2"
                    style={{
                      fontFamily: "'Kode Mono', monospace",
                      color: "#6B6B8D",
                    }}
                  >
                    Phone
                  </div>
                  {companyInfo.phones.map((phone) => (
                    <a
                      key={phone}
                      href={`tel:${phone.replace(/\s/g, "")}`}
                      className="no-underline block transition-colors duration-300 mb-1"
                      style={{
                        fontFamily: "'Kode Mono', monospace",
                        fontSize: "16px",
                        color: "#C4C4E0",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#A78BFA"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#C4C4E0"; }}
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div
                  className="w-12 h-12 flex items-center justify-center flex-shrink-0 rounded-lg"
                  style={{ background: "rgba(123,104,238,0.1)", border: "1px solid rgba(123,104,238,0.2)" }}
                >
                  <MapPin size={18} style={{ color: "#A78BFA" }} />
                </div>
                <div>
                  <div
                    className="text-xs uppercase tracking-wider mb-2"
                    style={{
                      fontFamily: "'Kode Mono', monospace",
                      color: "#6B6B8D",
                    }}
                  >
                    Office
                  </div>
                  <p
                    style={{
                      fontFamily: "'Kode Mono', monospace",
                      fontSize: "16px",
                      color: "#C4C4E0",
                      lineHeight: 1.6,
                      margin: 0,
                    }}
                  >
                    {companyInfo.address}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div>
                <label
                  className="block mb-2 text-xs uppercase tracking-wider"
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    color: "#6B6B8D",
                  }}
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#7B68EE";
                    e.target.style.background = "rgba(123,104,238,0.08)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgba(123,104,238,0.15)";
                    e.target.style.background = "rgba(123,104,238,0.05)";
                  }}
                />
              </div>

              <div>
                <label
                  className="block mb-2 text-xs uppercase tracking-wider"
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    color: "#6B6B8D",
                  }}
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@company.com"
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#7B68EE";
                    e.target.style.background = "rgba(123,104,238,0.08)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgba(123,104,238,0.15)";
                    e.target.style.background = "rgba(123,104,238,0.05)";
                  }}
                />
              </div>

              <div>
                <label
                  className="block mb-2 text-xs uppercase tracking-wider"
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    color: "#6B6B8D",
                  }}
                >
                  Company
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your Company"
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#7B68EE";
                    e.target.style.background = "rgba(123,104,238,0.08)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgba(123,104,238,0.15)";
                    e.target.style.background = "rgba(123,104,238,0.05)";
                  }}
                />
              </div>

              <div>
                <label
                  className="block mb-2 text-xs uppercase tracking-wider"
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    color: "#6B6B8D",
                  }}
                >
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell us about your project..."
                  style={{ ...inputStyle, resize: "vertical", minHeight: "140px" }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#7B68EE";
                    e.target.style.background = "rgba(123,104,238,0.08)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgba(123,104,238,0.15)";
                    e.target.style.background = "rgba(123,104,238,0.05)";
                  }}
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-3 border-none cursor-pointer mt-2 rounded-lg"
                style={{
                  background: isSubmitting ? "rgba(123,104,238,0.5)" : "#7B68EE",
                  color: "#FFFFFF",
                  padding: "16px 32px",
                  fontSize: "18px",
                  fontWeight: 500,
                  fontFamily: "'Kode Mono', monospace",
                  minHeight: "56px",
                  transition: "background 0.4s ease-in-out",
                  width: "100%",
                }}
                onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.background = "#6A58DD"; }}
                onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.background = "#7B68EE"; }}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
                <Send size={18} />
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
